import type { DuesDto, SettlementDecision, SettlementDto, SettlementsDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText } from '../../ui';
import { euro as money, ManualLink } from './Scadenzario';

const KIND: Record<SettlementDto['tipo'], string> = { pagamento: 'Pagamento', incasso: 'Incasso', giroconto: 'Giroconto' };
const euro = (v: string | null) => (v === null ? '' : `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

/**
 * Pagamenti, incassi e giroconti di una voce (ADR 0245): nel movimento ciò che salda, nella fattura o nella rata i
 * movimenti che la saldano. I collegamenti certi li ha fatti il sistema; le proposte si confermano o si scartano, e ogni
 * collegamento si può togliere (non torna).
 */
export function Settlements({ tipo, rif, settleable = false }: { tipo: string; rif: string; settleable?: boolean }) {
  const { isManager } = useTenant();
  const q = useTenantQuery<SettlementsDto>(['model', 'settlements', tipo, rif], `/model/settlements?${new URLSearchParams({ tipo, rif }).toString()}`, { enabled: isManager });
  const list = q.data?.collegamenti ?? [];
  if (!list.length && !(isManager && settleable)) return null;
  const proposals = list.filter((s) => s.stato === 'proposto').length;
  return (
    <Card>
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Pagamenti e incassi <Badge tone="blue">{list.length - proposals}</Badge>
        {proposals > 0 && <Badge tone="amber">{proposals} da confermare</Badge>}
      </h2>
      <ul className="mt-3 divide-y divide-slate-100">
        {list.map((s) => (
          <SettlementRow key={s.id} s={s} self={{ tipo, rif }} />
        ))}
      </ul>
      {isManager && settleable && <AddByHand tipo={tipo} rif={rif} />}
    </Card>
  );
}

/** Il collegamento a mano dalla scheda della fattura (ADR 0245, 0254): quanto resta dallo scadenzario, poi il movimento. */
function AddByHand({ tipo, rif }: { tipo: string; rif: string }) {
  const [open, setOpen] = useState(false);
  const dues = useTenantQuery<DuesDto>(['scadenze', ''], '/scadenze', { enabled: open });
  const item = dues.data?.voci.find((v) => v.tipo === tipo && v.rif === rif);
  return (
    <div className="mt-2 border-t border-slate-100 pt-2">
      <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => setOpen((x) => !x)}>
        {open ? 'Chiudi' : 'Collega a mano un pagamento o un incasso'}
      </Button>
      {open && dues.isSuccess && !item && <p className="mt-1 text-xs text-slate-500">Questa fattura non ha niente da saldare.</p>}
      {open && item && (
        <>
          <p className="mt-1 text-xs text-slate-500">Restano da saldare {money(item.residuo)}{item.proposto > 0 ? `, con ${money(item.proposto)} proposti da confermare` : ''}.</p>
          <ManualLink item={item} onDone={() => setOpen(false)} />
        </>
      )}
    </div>
  );
}

function SettlementRow({ s, self }: { s: SettlementDto; self: { tipo: string; rif: string } }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Dalla parte del movimento si vede ciò che salda; dalla parte della cosa, il movimento.
  const other = s.movimento.tipo === self.tipo && s.movimento.rif === self.rif ? s.cosa : s.movimento;
  const decide = async (body: SettlementDecision) => {
    setBusy(true);
    setError(null);
    try {
      await api(t(`/model/settlements/${s.id}`), { body });
      await queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model', 'settlements'] });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="text-slate-500">{KIND[s.tipo]}</span>
        <a className="font-medium text-accent hover:underline" href={`#/t/${tenant.id}/e/${other.tipo}/${encodeURIComponent(other.rif)}`}>
          {other.nome ?? other.rif}
        </a>
        {other.data && <span className="text-xs text-slate-500">del {new Date(other.data).toLocaleDateString('it-IT')}</span>}
        <span className="font-medium">{euro(s.importo)}</span>
        {s.stato === 'proposto' ? <Badge tone="amber">Da confermare</Badge> : s.origine === 'persona' ? <Badge tone="blue">Collegato da te</Badge> : s.stato === 'confermato' ? <Badge tone="blue">Confermato</Badge> : <Badge tone="green">Certo</Badge>}
      </div>
      {s.perche && <p className="mt-1 text-xs text-slate-500">{s.perche.charAt(0).toUpperCase() + s.perche.slice(1)}</p>}
      {isManager && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {s.stato === 'proposto' && (
            <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'confermato' })}>
              Conferma
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'scartato' })}>
            {s.stato === 'proposto' ? 'Scarta' : 'Togli'}
          </Button>
        </div>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </li>
  );
}
